import { cx } from "@shared/components/untitledui/util/cx"
import { getEditorJsonContent } from "@shared/lib/card-stack/prose-mirror-utils"
import type { UnifiedCardStack } from "@shared/models/card-stack"
import type { LinkDialogController } from "@shared/unified-tiptap/editor-ui/link/use-link-dialog"
import { cardStackExtensions } from "@shared/unified-tiptap/extensions"
import {
  EMPTY_COMMENT_ACTIONS,
  type DerivedPermissions,
  type UnifiedTiptapUiValue,
} from "@shared/unified-tiptap/unified-tiptap-ui-context"
import UnifiedTiptapRenderer from "@shared/unified-tiptap/UnifiedTiptapRenderer"
import { tracked, useSignalEffect } from "@supergrain/kernel/react"
import { useEditor } from "@tiptap/react"
import { useMemo } from "react"

/** This view never edits or comments, whatever the lesson's own permissions say. */
const READ_ONLY_PERMISSIONS: DerivedPermissions = { canEdit: false, canComment: false }

/** Nothing here can open the link dialog: Mod-K is an editing gesture, and the link hover
 * menu only renders for an editor that can edit. */
const CLOSED_LINK_DIALOG: LinkDialogController = {
  origin: null,
  open: () => {},
  close: () => {},
}

interface Props {
  lesson: UnifiedCardStack
  /** Applies where a bare title element exists; a lesson with content titles inside the editor. */
  lessonTitleClass?: string
  showOddMessageIfCardsAreNull?: boolean
  /** Whether to hide all Menu and Add buttons. */
  hideButtons?: boolean
}

export default tracked(function UnifiedLessonSimpleReadOnly({
  lesson,
  lessonTitleClass,
  showOddMessageIfCardsAreNull,
  hideButtons,
}: Props) {
  if (lesson.attributes.proseMirror) {
    return (
      <UnifiedLessonSimpleReadonlyInternal
        // Edits in place reach the editor through its signal effect; a document inserted anew in
        // this one's place is a different object, so it gets an editor of its own.
        key={documentKey(lesson)}
        lesson={lesson}
        hideButtons={hideButtons}
      />
    )
  } else {
    return (
      <div className="px-3">
        <div
          className={cx(
            "mt-3 mb-4.5 w-full grow border-b border-transparent text-[1.4rem] leading-[0.95] font-black tracking-[-0.2px] wrap-anywhere subpixel-antialiased",
            lessonTitleClass,
          )}
          data-test-lesson-title
        >
          {lesson.attributes.title}
        </div>
        <div>
          {showOddMessageIfCardsAreNull && (
            <div className="bg-white px-0 text-[0.9375rem]">
              <h3>
                The cards for this lesson had not been edited at this point. If you rollback to this
                version, the cards will be replaced with this class' current template.
              </h3>
            </div>
          )}
        </div>
      </div>
    )
  }
})

const UnifiedLessonSimpleReadonlyInternal = tracked(function UnifiedLessonProseMirror({
  lesson,
  hideButtons,
}: {
  lesson: UnifiedCardStack
  hideButtons?: boolean
}) {
  const editor = useEditor({
    extensions: cardStackExtensions({ openLinkDialog: null, filestackClient: null }),
    editable: false,
    content: readOnlyContent(lesson),
  })

  // The editor takes `content` only when it's created; later changes to the lesson land here.
  useSignalEffect(() => {
    const content = readOnlyContent(lesson)
    if (!editor.isDestroyed) editor.commands.setContent(content, { emitUpdate: false })
  })

  const floatingPortal = useMemo(
    () => document.getElementById("unified-tiptap-floating-portal")!,
    [],
  )

  const uiValue: Omit<UnifiedTiptapUiValue, "arbiter" | "setCardOverlayOpen"> = useMemo(
    () => ({
      permissions: READ_ONLY_PERMISSIONS,
      isNonInstructional: false,
      currentUserId: null,
      commentActions: EMPTY_COMMENT_ACTIONS,
      hideButtons,
    }),
    [hideButtons],
  )

  return (
    <div className="px-3">
      <UnifiedTiptapRenderer
        editor={editor}
        unifiedTiptapUiValue={uiValue}
        linkDialog={CLOSED_LINK_DIALOG}
        floatingPortal={floatingPortal}
      />
    </div>
  )
})

const documentKeys = new WeakMap<UnifiedCardStack, number>()
let nextDocumentKey = 0

function documentKey(lesson: UnifiedCardStack): number {
  let key = documentKeys.get(lesson)
  if (key === undefined) documentKeys.set(lesson, (key = nextDocumentKey++))
  return key
}

function readOnlyContent(lesson: UnifiedCardStack) {
  return getEditorJsonContent({
    title: lesson.attributes.title ?? "",
    content: lesson.attributes.proseMirror!,
  })
}
